import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

const FALLBACK_SERVICES = [
  { id: "svc-01", code: "SVC-01", testid: "service-card-diagnostics", name: "Advanced Diagnostics", price: "from $149", span: true,
    desc: "Dealer-level OBD, oscilloscope and thermal diagnostics across all German marques. Faults found fast, fixed right." },
  { id: "svc-02", code: "SVC-02", testid: "service-card-tuning", name: "Performance Tuning & ECU Reflash", price: "from $899", span: false,
    desc: "Stage 1–3 calibration, dyno-verified gains, factory-safe limits." },
  { id: "svc-03", code: "SVC-03", testid: "service-card-brakes", name: "Brake Service & Big-Brake Upgrades", price: "from $249", span: false,
    desc: "Pads, rotors, fluid and full BBK installs with track bedding." },
  { id: "svc-04", code: "SVC-04", testid: "service-card-maintenance", name: "Factory Scheduled Maintenance", price: "from $199", span: true,
    desc: "Logbook-stamped servicing to manufacturer spec — warranty intact, corners never cut." },
  { id: "svc-05", code: "SVC-05", testid: "service-card-suspension", name: "Suspension, Alignment & Corner Balance", price: "from $179", span: true,
    desc: "Coilovers, bushings, race alignment and corner weighting on our in-house scales." },
  { id: "svc-06", code: "SVC-06", testid: "service-card-engine", name: "Engine & Transmission Repair", price: "Quote", span: false,
    desc: "From timing chains to full rebuilds and PDK/DCT mechatronics." },
];

export function splitTitle(title) {
  const words = (title || "").split(" ").filter(Boolean);
  const at = Math.max(1, Math.ceil(words.length / 2));
  return [words.slice(0, at).join(" "), words.slice(at).join(" ")];
}

export default function Services({ onBook, content }) {
  const [services, setServices] = useState(FALLBACK_SERVICES);

  useEffect(() => {
    api.get("/services")
      .then((res) => { if (Array.isArray(res.data) && res.data.length) setServices(res.data); })
      .catch(() => {});
  }, []);

  const eyebrow = content?.services_eyebrow || "01 /// WHAT WE DO";
  const [titleA, titleB] = splitTitle(content?.services_title || "Service Menu");

  return (
    <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="services-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-4">{eyebrow}</p>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-100">
          {titleA} <span className="text-outline">{titleB}</span>
        </h2>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-3 gap-4" data-testid="services-grid">
        {services.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={s.testid}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative bg-panel border border-[#263342] p-7 sm:p-9 flex flex-col justify-between min-h-[240px] hover:border-crimson/50 hover:bg-[#1E2733] transition-colors ${s.span ? "md:col-span-2" : ""}`}
          >
            <div>
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-xs tracking-[0.25em] text-slate-500">{s.code}</span>
                <span className="font-mono text-xs tracking-[0.15em] text-crimson font-semibold uppercase">{s.price}</span>
              </div>
              <h3 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl text-slate-100 leading-tight">
                {s.name}
              </h3>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-lg">{s.desc}</p>
            </div>
            <button
              data-testid={`${s.testid}-book-button`}
              onClick={() => onBook({ service: s.name })}
              className="mt-8 self-start flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-slate-400 group-hover:text-crimson transition-colors"
            >
              Request this service
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
